import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DashboardLayout from "@/components/DashboardLayout";
import { StatusBadge, ApplicationPipeline } from "@/components/Shared";
import { api, formatApiError } from "@/lib/api";

const TICKET_STATUSES = ["Open", "Under Review", "Resolved"];
const PROPOSAL_STATUSES = ["Submitted", "Under Review", "Approved", "In Progress", "Completed", "Rejected"];

export default function AdminPortal() {
    const [overview, setOverview] = useState(null);
    const [users, setUsers] = useState([]);
    const [roleFilter, setRoleFilter] = useState("all");
    const [opps, setOpps] = useState({ projects: [], internships: [] });
    const [oppFilter, setOppFilter] = useState("pending");
    const [applications, setApplications] = useState([]);
    const [payments, setPayments] = useState([]);
    const [proposals, setProposals] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [activeTicket, setActiveTicket] = useState(null);
    const [reply, setReply] = useState("");

    const loadOverview = () => api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    const loadUsers = (role = roleFilter) => api.get(`/admin/users${role !== "all" ? `?role=${role}` : ""}`).then((r) => setUsers(r.data)).catch(() => {});
    const loadOpps = (s = oppFilter) => api.get(`/admin/opportunities${s !== "all" ? `?status=${s}` : ""}`).then((r) => setOpps(r.data)).catch(() => {});
    const loadApplications = () => api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
    const loadPayments = () => api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    const loadProposals = () => api.get("/admin/proposals").then((r) => setProposals(r.data)).catch(() => {});
    const loadTickets = () => api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});

    useEffect(() => { loadOverview(); loadUsers("all"); loadOpps("pending"); loadApplications(); loadPayments(); loadProposals(); loadTickets(); }, []);

    const verifyUser = async (id, verified) => {
        await api.put(`/admin/users/${id}/verify`, { verified });
        toast.success(verified ? "User verified" : "Verification removed");
        loadUsers(); loadOverview();
    };

    const setOppStatus = async (kind, id, status) => {
        await api.put(`/admin/opportunities/${kind}/${id}/status`, { status });
        toast.success(`Opportunity ${status}`);
        loadOpps(); loadOverview();
    };

    const setProposalStatus = async (id, status) => {
        await api.put(`/admin/proposals/${id}/status`, { status });
        toast.success(`Proposal ${status}`);
        loadProposals();
    };

    const openTicket = async (t) => {
        const { data } = await api.get(`/tickets/${t.id}`);
        setActiveTicket(data);
    };

    const sendReply = async () => {
        if (!reply.trim()) return;
        await api.post(`/tickets/${activeTicket.id}/messages`, { message: reply });
        setReply("");
        openTicket(activeTicket);
    };

    const setTicketStatus = async (id, status) => {
        await api.put(`/tickets/${id}/status`, { status });
        toast.success(`Ticket ${status}`);
        loadTickets();
        if (activeTicket?.id === id) setActiveTicket({ ...activeTicket, status });
    };

    const statCards = overview ? [
        { label: "Students", value: overview.students },
        { label: "Faculty", value: overview.faculty },
        { label: "Organizations", value: overview.organizations },
        { label: "Pending Verifications", value: overview.pending_verifications },
        { label: "Pending Opportunities", value: overview.pending_opportunities },
        { label: "Applications", value: overview.applications },
        { label: "Open Tickets", value: overview.open_tickets },
        { label: "Proposals", value: overview.proposals },
        { label: "Revenue (₹)", value: overview.revenue_inr.toLocaleString("en-IN") },
    ] : [];

    return (
        <DashboardLayout title="Admin Portal">
            <div className="space-y-8" data-testid="admin-portal">
                <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="admin-stats">
                    {statCards.map((c) => (
                        <div key={c.label} className="card-soft p-5">
                            <p className="font-display text-2xl font-bold text-primary">{c.value}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{c.label}</p>
                        </div>
                    ))}
                </div>

                <Tabs defaultValue="users">
                    <TabsList data-testid="admin-tabs" className="flex-wrap">
                        <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
                        <TabsTrigger value="opportunities" data-testid="tab-opportunities">Opportunities</TabsTrigger>
                        <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
                        <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
                        <TabsTrigger value="proposals" data-testid="tab-proposals">Proposals</TabsTrigger>
                        <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets</TabsTrigger>
                    </TabsList>

                    <TabsContent value="users" className="mt-6 space-y-4">
                        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); loadUsers(v); }}>
                            <SelectTrigger data-testid="admin-role-filter" className="w-52 rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All roles</SelectItem>
                                <SelectItem value="student">Students</SelectItem>
                                <SelectItem value="faculty">Faculty</SelectItem>
                                <SelectItem value="organization">Organizations</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="card-soft overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-border/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Verified</th><th className="p-4">Action</th>
                                </tr></thead>
                                <tbody>
                                    {users.map((u) => (
                                        <tr key={u.id} className="border-b border-border/20 last:border-0" data-testid={`user-row-${u.id}`}>
                                            <td className="p-4 font-medium">{u.name}</td>
                                            <td className="p-4 text-xs text-muted-foreground">{u.email}</td>
                                            <td className="p-4 capitalize">{u.role}</td>
                                            <td className="p-4">{u.verified ? <span className="flex items-center gap-1 text-xs font-semibold text-tealx"><BadgeCheck className="h-4 w-4" /> Yes</span> : <span className="text-xs text-muted-foreground">No</span>}</td>
                                            <td className="p-4">
                                                {u.role !== "admin" && (
                                                    <Button data-testid={`verify-user-${u.id}`} size="sm" variant={u.verified ? "outline" : "default"} className="rounded-full"
                                                        onClick={() => verifyUser(u.id, !u.verified)}>
                                                        {u.verified ? "Unverify" : "Verify"}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>

                    <TabsContent value="opportunities" className="mt-6 space-y-4">
                        <Select value={oppFilter} onValueChange={(v) => { setOppFilter(v); loadOpps(v); }}>
                            <SelectTrigger data-testid="admin-opp-filter" className="w-52 rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="pending">Pending approval</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                                <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="grid gap-4 md:grid-cols-2">
                            {[...opps.internships.map((i) => ({ ...i, kind: "internship" })), ...opps.projects.map((p) => ({ ...p, kind: "project" }))].map((o) => (
                                <div key={o.id} className="card-soft p-5" data-testid={`opp-row-${o.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="font-display text-sm font-semibold">{o.title}</p>
                                            <p className="mt-1 text-xs text-muted-foreground">{o.kind} · {o.domain} · by {o.posted_by_name}</p>
                                        </div>
                                        <StatusBadge status={o.status} />
                                    </div>
                                    <div className="mt-4 flex gap-2">
                                        <Button data-testid={`approve-${o.id}`} size="sm" className="rounded-full" onClick={() => setOppStatus(o.kind, o.id, "approved")}>Approve</Button>
                                        <Button data-testid={`reject-${o.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => setOppStatus(o.kind, o.id, "rejected")}>Reject</Button>
                                    </div>
                                </div>
                            ))}
                            {opps.internships.length + opps.projects.length === 0 && <p className="text-sm text-muted-foreground">Nothing here.</p>}
                        </div>
                    </TabsContent>

                    <TabsContent value="applications" className="mt-6">
                        <div className="space-y-4">
                            {applications.map((a) => (
                                <div key={a.id} className="card-soft p-5" data-testid={`admin-application-${a.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <p className="font-display text-sm font-semibold">{a.internship_title}</p>
                                            <p className="mt-1 text-xs text-muted-foreground">{a.student_name} · {a.student_email} · {new Date(a.created_at).toLocaleDateString()}</p>
                                        </div>
                                        <StatusBadge status={a.payment_status} />
                                    </div>
                                    <div className="mt-4"><ApplicationPipeline status={a.status} /></div>
                                </div>
                            ))}
                            {applications.length === 0 && <p className="text-sm text-muted-foreground">No applications yet.</p>}
                        </div>
                    </TabsContent>

                    <TabsContent value="payments" className="mt-6">
                        <div className="card-soft overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-border/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-4">Order</th><th className="p-4">Student</th><th className="p-4">Program</th><th className="p-4">Amount</th><th className="p-4">Status</th>
                                </tr></thead>
                                <tbody>
                                    {payments.map((p) => (
                                        <tr key={p.id} className="border-b border-border/20 last:border-0" data-testid={`payment-row-${p.id}`}>
                                            <td className="p-4 font-mono text-xs">{p.order_id}</td>
                                            <td className="p-4">{p.user_name}</td>
                                            <td className="p-4">{p.internship_title}</td>
                                            <td className="p-4">₹{(p.amount_paise / 100).toLocaleString("en-IN")}</td>
                                            <td className="p-4"><StatusBadge status={p.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {payments.length === 0 && <p className="p-6 text-sm text-muted-foreground">No payments yet. Razorpay keys are not configured yet — add them to enable checkout.</p>}
                        </div>
                    </TabsContent>

                    <TabsContent value="proposals" className="mt-6">
                        <div className="grid gap-4 md:grid-cols-2">
                            {proposals.map((p) => (
                                <div key={p.id} className="card-soft p-5" data-testid={`admin-proposal-${p.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="font-display text-sm font-semibold">{p.title}</p>
                                            <p className="mt-1 text-xs text-muted-foreground">{p.student_name} · {p.domain} · {p.duration}</p>
                                        </div>
                                        <StatusBadge status={p.status} />
                                    </div>
                                    <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{p.idea}</p>
                                    <Select value={p.status} onValueChange={(v) => setProposalStatus(p.id, v)}>
                                        <SelectTrigger data-testid={`proposal-status-${p.id}`} className="mt-4 w-48 rounded-lg"><SelectValue /></SelectTrigger>
                                        <SelectContent>{PROPOSAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            ))}
                            {proposals.length === 0 && <p className="text-sm text-muted-foreground">No proposals yet.</p>}
                        </div>
                    </TabsContent>

                    <TabsContent value="tickets" className="mt-6">
                        <div className="space-y-4">
                            {tickets.map((t) => (
                                <div key={t.id} className="card-soft flex flex-wrap items-center justify-between gap-4 p-5" data-testid={`admin-ticket-${t.ticket_no}`}>
                                    <button onClick={() => openTicket(t)} className="text-left" data-testid={`admin-open-ticket-${t.ticket_no}`}>
                                        <p className="font-mono text-xs text-muted-foreground">#{t.ticket_no}</p>
                                        <p className="mt-1 font-display text-sm font-semibold">{t.subject}</p>
                                        <p className="text-xs text-muted-foreground">{t.user_name} · {t.category}</p>
                                    </button>
                                    <div className="flex items-center gap-3">
                                        <StatusBadge status={t.status} />
                                        <Select value={t.status} onValueChange={(v) => setTicketStatus(t.id, v)}>
                                            <SelectTrigger data-testid={`ticket-status-${t.ticket_no}`} className="w-40 rounded-lg"><SelectValue /></SelectTrigger>
                                            <SelectContent>{TICKET_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            ))}
                            {tickets.length === 0 && <p className="text-sm text-muted-foreground">No tickets.</p>}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <Dialog open={!!activeTicket} onOpenChange={() => setActiveTicket(null)}>
                <DialogContent className="max-w-lg" data-testid="admin-ticket-dialog">
                    {activeTicket && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">#{activeTicket.ticket_no} — {activeTicket.subject} <StatusBadge status={activeTicket.status} /></DialogTitle>
                            </DialogHeader>
                            <div className="max-h-80 space-y-3 overflow-y-auto pt-2">
                                {activeTicket.messages.map((m, i) => (
                                    <div key={i} className={`rounded-xl p-4 text-sm ${m.sender === "admin" ? "bg-primary/5" : "bg-muted"}`} data-testid={`admin-ticket-message-${i}`}>
                                        <p className="text-xs font-semibold text-muted-foreground">{m.sender_name} · {new Date(m.at).toLocaleString()}</p>
                                        <p className="mt-1">{m.text}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex gap-2">
                                <Input data-testid="admin-ticket-reply-input" placeholder="Reply to student..." value={reply} onChange={(e) => setReply(e.target.value)} className="rounded-lg" />
                                <Button data-testid="admin-ticket-reply-button" size="icon" onClick={sendReply}><Send className="h-4 w-4" /></Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
